import React, { Component } from "react";
import Input from "@cloudscape-design/components/input";
import Grid from "@cloudscape-design/components/grid";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import FormField from "@cloudscape-design/components/form-field";
import Multiselect from "@cloudscape-design/components/multiselect";

class GamesSweepstakesGameTab extends Component {
	static mapSweepstakesToFormValues(sweepstakes, currentOption) {
		const findOption = (options, value) => options.find((option) => option.value === value) || null;
		const rtpLabelTypeOption = currentOption.rtpLabelType[0] || { value: "international", label: "international" };

		return {
			poster: sweepstakes.poster || "",
			name: sweepstakes.nameText || "",
			nameType: findOption(currentOption.nameType, sweepstakes.nameType),
			description: sweepstakes.descriptionText || "",
			descriptionType: findOption(currentOption.descriptionType, sweepstakes.descriptionType),
			rtp: sweepstakes.rtp ?? "",
			rtpLabelText: sweepstakes.rtpLabelText || "",
			rtpLabelType: rtpLabelTypeOption,
			categoryID: (sweepstakes.categoryIDs || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
			providerName: sweepstakes.provider?.name || "",
			providerLink: sweepstakes.provider?.link || "",
		};
	}

	mapFormToSweepstakesPayload = (values) => ({
		poster: values.poster || "",
		nameText: values.name || "",
		nameType: values.nameType?.value || values.nameType || "",
		descriptionText: values.description || "",
		descriptionType: values.descriptionType?.value || values.descriptionType || "",
		rtp: values.rtp === "" ? 0 : Number(values.rtp),
		rtpLabelText: values.rtpLabelText || "",
		categoryIDs: (values.categoryID || []).map((category) => Number(category?.type ?? category)).filter((id) => !Number.isNaN(id)),
		provider: {
			name: values.providerName || "",
			link: values.providerLink || "",
		},
	});

	getCategoryOptions = () => {
		const { config } = this.props;

		if (!config.game?.options?.category) {
			return [];
		}

		return config.game.options.category.slice(1).map((option) => {
			const rawName = typeof option.value === "string" ? option.value : "";
			const categoryName = rawName.replace(/\s\(\d+\)$/, "").trim() || `#${option.type}`;
			return {
				...option,
				labelTag: `${categoryName} (#${option.type})`,
			};
		});
	};

	getSelectedCategoryOptions = (selectedCategories, categoryOptions) => {
		return (selectedCategories || [])
			.map((category) => {
				const categoryId = Number(category?.type ?? category);
				if (Number.isNaN(categoryId)) return null;
				const fromOptions = categoryOptions.find((option) => Number(option.type) === categoryId);
				if (fromOptions) return fromOptions;
				return {
					type: categoryId,
					value: `#${categoryId}`,
					labelTag: `#${categoryId}`,
				};
			})
			.filter(Boolean);
	};

	setModalError = (text, code = 500) => {
		this.props.setGamesSweepstakesModal((prev) => ({
			...prev,
			loaded: true,
			error: {
				text,
				code,
				timestamp: Date.now(),
			},
		}));
	};

	updateField = (key, value) => {
		this.props.setGamesSweepstakesModal((prev) => ({
			...prev,
			values: {
				...prev.values,
				[key]: value,
			},
		}));
	};

	isPngFile = (file) => file && (file.type === "image/png" || file.name.toLowerCase().endsWith(".png"));

	readPngFile = (file) => {
		if (!this.isPngFile(file)) {
			this.setModalError("Poster must be a PNG file", 400);
			return;
		}

		const reader = new FileReader();
		reader.onload = (event) => {
			this.props.setGamesSweepstakesModal((prev) => ({
				...prev,
				values: {
					...prev.values,
					poster: event.target.result,
				},
				error: { text: "", code: 0, timestamp: 0 },
			}));
		};
		reader.readAsDataURL(file);
	};

	handlePosterDrop = (event) => {
		event.preventDefault();
		if (!event.dataTransfer.files?.length) return;
		this.readPngFile(event.dataTransfer.files[0]);
	};

	handlePosterDragOver = (event) => {
		event.preventDefault();
	};

	handlePosterFileChange = (event) => {
		if (!event.target.files?.length) return;
		this.readPngFile(event.target.files[0]);
	};

	validatePayload = (payload) => {
		if (!payload.poster) return "Poster URL is required";
		if (!payload.nameText) return "Name is required";
		if (!payload.nameType) return "Name text type is required";
		if (!payload.descriptionText) return "Description is required";
		if (!payload.descriptionType) return "Description text type is required";
		if (!Number.isFinite(payload.rtp)) return "RTP must be a valid number";
		if (!payload.provider?.name) return "Provider name is required";
		if (!payload.provider?.link) return "Provider link is required";
		if (!Array.isArray(payload.categoryIDs) || payload.categoryIDs.length === 0) return "At least one category is required";

		const link = payload.provider.link.trim();
		if (link.startsWith("/")) return "Provider link must not start with /";
		if (!/^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/.test(link)) {
			return "Provider link must be internal path like lobby/0/1";
		}

		return "";
	};

	save = async () => {
		const { gamesSweepstakesModal, setGamesSweepstakesModal, Interface } = this.props;
		const payload = this.mapFormToSweepstakesPayload(gamesSweepstakesModal.values);
		const validationError = this.validatePayload(payload);

		if (validationError) {
			this.setModalError(validationError, 400);
			Interface.addNoty("Error", validationError);
			return;
		}

		setGamesSweepstakesModal((prev) => ({
			...prev,
			loaded: false,
			error: { text: "", code: 0, timestamp: 0 },
		}));

		try {
			await Interface.updateSweepstakes(payload);
			setGamesSweepstakesModal((prev) => ({
				...prev,
				loaded: true,
				error: { text: "", code: 0, timestamp: 0 },
			}));
			Interface.addNoty("Success", "Sweepstakes game settings were successfully updated");
		} catch (error) {
			this.setModalError(error.text || error.toString(), error.code || 500);
			Interface.addNoty("Error", error.text || error.toString());
		}
	};

	render() {
		const { gamesSweepstakesModal } = this.props;
		const { values, loaded, currentOption } = gamesSweepstakesModal;
		const categoryOptions = this.getCategoryOptions();
		const selectedCategoryOptions = this.getSelectedCategoryOptions(values.categoryID, categoryOptions);

		return (
			<SpaceBetween size="m">
				<FormField label="Game poster">
					<SpaceBetween size="xs">
						<div
							onDrop={this.handlePosterDrop}
							onDragOver={this.handlePosterDragOver}
							style={{
								border: "1px dashed #879596",
								borderRadius: 8,
								padding: 12,
								textAlign: "center",
							}}>
							Drop PNG here
						</div>
						<input type="file" accept=".png,image/png" onChange={this.handlePosterFileChange} disabled={!loaded} />
						{values.poster ? <img src={values.poster} alt="Sweepstakes poster" style={{ maxWidth: 200, borderRadius: 8 }} /> : null}
					</SpaceBetween>
				</FormField>

				<Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
					<FormField label="Name">
						<Input value={values.name} inputMode="text" type="text" placeholder="Name" onChange={(event) => this.updateField("name", event.detail.value)} disabled={!loaded} />
					</FormField>
					<FormField label="Name text type">
						<Select filteringType="auto" placeholder="Select text type" selectedOption={values.nameType} onChange={({ detail }) => this.updateField("nameType", detail.selectedOption)} options={currentOption.nameType} disabled={!loaded} />
					</FormField>
				</Grid>

				<Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
					<FormField label="Description">
						<Input value={values.description} inputMode="text" type="text" placeholder="Description" onChange={(event) => this.updateField("description", event.detail.value)} disabled={!loaded} />
					</FormField>
					<FormField label="Description text type">
						<Select filteringType="auto" placeholder="Select text type" selectedOption={values.descriptionType} onChange={({ detail }) => this.updateField("descriptionType", detail.selectedOption)} options={currentOption.descriptionType} disabled={!loaded} />
					</FormField>
				</Grid>

				<FormField label="RTP">
					<Input value={values.rtp} inputMode="decimal" type="number" placeholder="RTP" onChange={(event) => this.updateField("rtp", event.detail.value)} disabled={!loaded} />
				</FormField>

				<Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
					<FormField label="Rtp label translation">
						<Input value={values.rtpLabelText} inputMode="text" type="text" placeholder="Rtp label translation" onChange={(event) => this.updateField("rtpLabelText", event.detail.value)} disabled={!loaded} />
					</FormField>
					<FormField label="Rtp label text type">
						<Select selectedOption={values.rtpLabelType || currentOption.rtpLabelType[0]} options={currentOption.rtpLabelType} disabled={true} />
					</FormField>
				</Grid>

				<FormField label="Category">
					<Multiselect filteringType="auto" selectedOptions={selectedCategoryOptions} onChange={({ detail }) => this.updateField("categoryID", detail.selectedOptions)} placeholder="Category" options={categoryOptions} disabled={!loaded} />
				</FormField>

				<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
					<FormField label="Provider">
						<Input value={values.providerName} inputMode="text" type="text" placeholder="Provider" onChange={(event) => this.updateField("providerName", event.detail.value)} disabled={!loaded} />
					</FormField>
					<FormField label="Provider link">
						<Input value={values.providerLink} inputMode="text" type="text" placeholder="lobby/0/1" onChange={(event) => this.updateField("providerLink", event.detail.value)} disabled={!loaded} />
					</FormField>
				</Grid>
			</SpaceBetween>
		);
	}
}

export default GamesSweepstakesGameTab;
